import React from 'react';
import { Dialog } from '@mui/material';
import { WBContainer, WBFlex } from '@admiin-com/ds-web';
import SignPayLayout from '../layout/SignPayLayout';
import { t } from 'i18next';
import { TaskPropertyDesktop } from '../layout/components/TaskProperty/TaskPropertyDesktop';
import {
  frontDateFromBackendDate,
  numberToCurrency,
} from '@admiin-com/ds-common';
import { TaskPropertyMobile } from '../layout/components/TaskProperty/TaskPropertyMobile';
import { TaskDetailMulti } from '../../components/TaskDetail/TaskDetailMulti';
import TaskPdfSignature from '../../../components/TaskPdfSignature/TaskPdfSignature';
import { PaymentContainer } from '../../../components/PaymentContainer/PaymentContainer';
import { PaymentForm } from './components/PaymentForm';
import { useTaskFromToName } from '../../../hooks/useTaskToName/useTaskToName';
import { Task } from '@admiin-com/ds-graphql';
import { useGetTask } from '../../hooks/useGetTask/useGetTask';

export interface PayFlowModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tasks: Task[];
}

export const PayFlowModal: React.FC<PayFlowModalProps> = ({
  open,
  onClose,
  tasks,
  onSuccess,
}) => {
  const { from, to, futureInputTo, futureInputFrom } = useTaskFromToName(
    tasks[0]
  );
  const payTo = futureInputTo ?? to;
  const payFrom = futureInputFrom ?? from;

  const { task: realTask } = useGetTask({
    id: tasks[0].id,
    forceRefresh: true,
  });

  const properties =
    tasks.length > 1
      ? []
      : [
          { label: t('from', { ns: 'taskbox' }), value: payFrom ?? '' },
          { label: t('to', { ns: 'taskbox' }), value: payTo ?? '' },
          {
            label: t('dueAt', { ns: 'taskbox' }),
            value: frontDateFromBackendDate(
              tasks[0]?.futureLinkedTask?.dueAt ?? tasks[0].dueAt
            ),
          },
          {
            label: t('amount', { ns: 'taskbox' }),
            value: numberToCurrency(
              tasks[0]?.futureLinkedTask?.amount ?? tasks[0].amount ?? 0
            ),
          },
          {
            label: t('reference', { ns: 'taskbox' }),
            value:
              tasks[0]?.futureLinkedTask?.reference ?? tasks[0].reference ?? '',
          },
        ];

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <SignPayLayout
        title={t('Request Bill', { ns: 'tasks' })}
        onClose={onClose}
      >
        <TaskPropertyDesktop properties={properties} />
        <WBContainer
          maxWidth="xl"
          sx={{
            px: [0, 4],
            py: 2,
            width: '100%',
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <TaskPropertyMobile properties={properties} />
          <WBFlex flex={1} justifyContent="center" alignItems="start" p={2}>
            {tasks.length > 1 ? (
              <TaskDetailMulti tasks={tasks} />
            ) : (
              <TaskPdfSignature task={realTask} />
            )}
          </WBFlex>
          <WBFlex flex={1} p={2}>
            <PaymentContainer
              tasks={tasks}
              onSuccess={() => {
                onSuccess();
                onClose();
              }}
            >
              <PaymentForm tasks={tasks} />
            </PaymentContainer>
          </WBFlex>
        </WBContainer>
      </SignPayLayout>
    </Dialog>
  );
};
